import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, IconButton, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';

// Same two-pane preview/send pattern as DoEmailDialog, but scoped to stores.
// Content: open items from each store's latest walkthrough. Stores with no
// walkthrough in the last 90 days are omitted server-side.
export default function StoreEmailDialog({ open, onClose, firebaseUser, getToken }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stores, setStores] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [activeId, setActiveId] = useState(null);
  const [replyTo, setReplyTo] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResults, setSendResults] = useState(null);

  const activeStore = useMemo(
    () => stores.find((s) => s.storeId === activeId) || stores[0] || null,
    [stores, activeId]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSendResults(null);
    try {
      const token = await getToken();
      const { data } = await axios.get('/hd-api/facilities-review/store-email-preview', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = data?.stores || [];
      setStores(list);
      setSelectedIds(new Set(list.map((s) => s.storeId)));
      setActiveId(list[0]?.storeId || null);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (!open) return;
    setReplyTo(firebaseUser?.email || '');
    load();
  }, [open, load, firebaseUser]);

  const toggle = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const toggleAll = () => {
    if (selectedIds.size === stores.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(stores.map((s) => s.storeId)));
  };

  const handleSend = async () => {
    if (!selectedIds.size || !replyTo) return;
    setSending(true);
    setError(null);
    try {
      const token = await getToken();
      const { data } = await axios.post(
        '/hd-api/facilities-review/store-email-send',
        { storeIds: Array.from(selectedIds), replyTo },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSendResults(data?.results || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const errorCount = (sendResults || []).filter((r) => r.status !== 'sent').length;
  const successCount = (sendResults || []).filter((r) => r.status === 'sent').length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <span>Send Store Follow-up Emails</span>
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, height: 640 }}>
        {loading && (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
        )}
        {error && !loading && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

        {!loading && !error && stores.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No stores with a walkthrough in the last 90 days.
            </Typography>
          </Box>
        )}

        {!loading && !error && stores.length > 0 && (
          <Box sx={{ display: 'flex', height: '100%' }}>
            <Box sx={{ width: 320, borderRight: '1px solid', borderColor: 'divider', overflow: 'auto' }}>
              <List dense disablePadding>
                <ListItem sx={{ bgcolor: 'grey.50' }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox
                      edge="start"
                      size="small"
                      checked={selectedIds.size === stores.length}
                      indeterminate={selectedIds.size > 0 && selectedIds.size < stores.length}
                      onChange={toggleAll}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${selectedIds.size} of ${stores.length} selected`}
                    primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                  />
                </ListItem>
                <Divider />
                {stores.map((s) => (
                  <ListItem key={s.storeId} disablePadding sx={{
                    bgcolor: activeStore?.storeId === s.storeId ? 'action.selected' : undefined,
                  }}>
                    <ListItemIcon sx={{ minWidth: 36, pl: 2 }}>
                      <Checkbox
                        edge="start"
                        size="small"
                        checked={selectedIds.has(s.storeId)}
                        onChange={() => toggle(s.storeId)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </ListItemIcon>
                    <ListItemButton onClick={() => setActiveId(s.storeId)}>
                      <ListItemText
                        primary={s.storeName}
                        secondary={`${s.openItemsCount} open · ${s.recipient}`}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">To</Typography>
                <Typography variant="body2">{activeStore?.recipient}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Subject</Typography>
                <Typography variant="body2" fontWeight={500}>{activeStore?.subject}</Typography>
              </Box>
              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                {activeStore && (
                  <iframe
                    title="email preview"
                    srcDoc={activeStore.htmlBody}
                    style={{ border: 0, width: '100%', height: '100%' }}
                    sandbox=""
                  />
                )}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Reply-to"
          size="small"
          value={replyTo}
          onChange={(e) => setReplyTo(e.target.value)}
          sx={{ minWidth: 280 }}
          disabled={sending}
        />
        <Box sx={{ flex: 1 }} />
        {sendResults && (
          <Alert severity={errorCount ? 'warning' : 'success'} sx={{ py: 0, px: 1 }}>
            {successCount} sent{errorCount ? ` · ${errorCount} failed` : ''}
          </Alert>
        )}
        <Button onClick={onClose} disabled={sending}>Close</Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={sending || loading || !selectedIds.size || !replyTo}
          startIcon={sending ? <CircularProgress size={14} /> : <SendIcon />}
        >
          Send to {selectedIds.size} store{selectedIds.size === 1 ? '' : 's'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
